<script setup>
import { computed, reactive } from 'vue';
import Message from './Message.vue';
import DateSeparator from './DateSeparator.vue';
import { MESSAGE_TYPES } from './constants.js';
import { tinCuoiGuiDi, mocDocCuoi } from './readReceipt.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
// BonBoZ: cam xuc Zalo di vao duoi dang ghi chu rieng — xem zaloReaction.js
import { collectZaloReactions } from './zaloReaction';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

// BonBoZ: cầu nối Zalo gửi mỗi lần thả cảm xúc vào thành MỘT GHI CHÚ RIÊNG,
// kèm `in_reply_to` trỏ về tin được thả. Gom lại ở đây rồi giấu ghi chú đi;
// `Message.vue` vẽ chúng thành chip dính vào đúng tin gốc.
const zaloReactions = computed(() => collectZaloReactions(allMessages.value));

// Danh sách THẬT SỰ được vẽ. Mọi thứ tính theo vị trí — vạch ngăn ngày, nhóm
// tin liền nhau — phải chạy trên danh sách này chứ không phải `allMessages`,
// nếu không một ghi chú cảm xúc đã bị giấu vẫn cắt cụm tin làm đôi.
const visibleMessages = computed(() =>
  allMessages.value.filter(
    message => !zaloReactions.value.reactionNoteIds.has(message.id)
  )
);

// BonBoZ: Chatwoot không có khái niệm nhóm tin theo ngày, phải tự đánh dấu tin mở đầu mỗi ngày.
const dayStartMessageIds = computed(() => {
  const ids = new Set();
  let previousDay = null;
  visibleMessages.value.forEach(message => {
    const day = new Date(message.createdAt * 1000).toDateString();
    if (day !== previousDay) ids.add(message.id);
    previousDay = day;
  });
  return ids;
});

// BonBoZ: dấu "đã nhận" đeo ở tin gửi đi cuối cùng, avatar khách đeo ở mốc đọc.
// Chỉ danh sách mới biết tin nào là cuối — xem readReceipt.js.
const lastOutgoingId = computed(() => tinCuoiGuiDi(visibleMessages.value));
const lastReadOutgoingId = computed(() => mocDocCuoi(visibleMessages.value));

const currentChat = useMapGetter('getSelectedChat');

// BonBoZ: avatar đeo ở mốc đọc là avatar KHÁCH, không phải người gửi tin đó.
const readBy = computed(() => {
  const sender = currentChat.value?.meta?.sender;
  if (!sender) return null;

  return { name: sender.name ?? '', src: sender.thumbnail ?? '' };
});

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <!-- BonBoZ: container cho layout của Message.vue. Dùng container query thay vì
       media query `md:` vì bề ngang khung chat khác xa viewport — mở đủ 3 cột
       trên màn 1920px thì khung chat chỉ còn ~500px.
       Đặt ở list chứ không ở từng row: `container-type` sinh stacking context
       mới, đặt ở row sẽ khiến chip cảm xúc (tràn 8px xuống dưới bubble) bị row
       kế tiếp che. -->
  <ul
    class="px-4 bg-n-surface-1 [container-name:khung-chat] [container-type:inline-size]"
  >
    <slot name="beforeAll" />
    <template v-for="(message, index) in visibleMessages" :key="message.id">
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <DateSeparator
        v-if="dayStartMessageIds.has(message.id)"
        :timestamp="message.createdAt"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, visibleMessages)"
        :zalo-reactions="zaloReactions.reactionsByTarget.get(message.id)"
        :is-last-outgoing="message.id === lastOutgoingId"
        :is-read-watermark="message.id === lastReadOutgoingId"
        :read-by="readBy"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
